/**
 * Holds the Avro Events
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');

module.exports = {
  initialize: function() {
    this.__resetStore();

    this.on(actionTypes.FINALIZE_BATCH_SNAPSHOT, this.__finalizeSnapshot);
    this.on(actionTypes.REGISTER_PREVIOUS_BATCH, this.__registerPreviousBatch);
    this.on(actionTypes.REGISTER_TRACKER_VISITOR, this.__registerVisitor);
    this.on(actionTypes.REGISTER_TRACKER_EVENT, this.__registerEvent);
    this.on(actionTypes.REGISTER_TRACKER_DECISION, this.__registerDecision);
    this.on(actionTypes.RESET_TRACKER_EVENTS, this.__clearEvents);
    this.on(actionTypes.RESET_TRACKER_STORE, this.__resetStore);
    this.on(actionTypes.RESET_TRACKER_PREVIOUS_BATCHES, this.__resetPreviousBatches);
    this.on(actionTypes.SET_TRACKER_POLLING, this.__setPolling);
    this.on(actionTypes.SET_TRACKER_BATCHING, this.__setBatching);
    this.on(actionTypes.SET_TRACKER_USE_BEACON, this.__setUseBeacon);
    this.on(actionTypes.SET_TRACKER_SEND_EVENTS, this.__setCanSend);
    this.on(actionTypes.SET_TRACKER_PERSISTABLE_STATE, this.__setPersistableState);
    this.on(actionTypes.SET_TRACKER_DIRTY, this.__setDirty);
    this.on(actionTypes.UPDATE_TRACKER_VISITOR_ATTRIBUTES, this.__updateAttributes);
  },

  /**
   * Get persistable state
   *
   * **Note**: does NOT clone, don't expose or modify
   *
   * @return {Object|null} Persistable state for the tracker, or null if no "dirty" changes
   */
  getPersistableState: function(): any|null {
    if (!this.__state.isDirty) {
      return null;
    }
    // TODO(tyler): need to persist "previous visitors" as well
    if (!this.hasEventsToSend() && !this.hasPreviousBatchesToSend()) {
      return {};
    }
    return {
      data: this.__state.data,
      decisions: this.__state.decisions,
      decisionEvents: this.__state.decisionEvents,
      previousBatches: this.__state.previousBatches,
    };
  },

  __setPersistableState: function(payload) {
    if (!_.isEmpty(this.__state.data) && !_.isEmpty(payload.data)) {
      this.__finalizeSnapshot();
      this.__state.previousBatches.push(this.getEventBatch());
    }

    this.__state.data = payload.data || {};
    this.__state.decisions = payload.decisions || [];
    this.__state.decisionEvents = payload.decisionEvents || [];

    if (!_.isEmpty(this.__state.previousBatches) && !_.isEmpty(payload.previousBatches)) {
      // preserve existing previousBatches
      this.__state.previousBatches = this.__state.previousBatches.concat(payload.previousBatches);
    } else {
      this.__state.previousBatches = payload.previousBatches || [];
    }

    this.emitChange();
  },

  __setDirty: function(isDirty) {
    this.__state.isDirty = isDirty;
    this.emitChange();
  },

  /**
   * Registers a non-decision event with payload
   * Backfills the existing decisions and new decision events
   * @param {Object} payload
   * @param {BatchEventTicket} payload.event
   * @param {BatchDecisionTicket[]} payload.decisions
   * @private
   */
  __registerEvent: function(payload:any) {
    if (__INVARIANT__) {
      require('core/utils/invariant').isType('BatchEventTicket', payload.event);
      if (!payload.decisions) {
        throw new Error('decisions array is required');
      }
      _.forEach(payload.decisions, function(decision) {
        require('core/utils/invariant').isType('BatchDecisionTicket', decision);
      });
    }

    // After snapshots are reset or first time an event is added
    var currentVisitor = this.__getCurrentVisitor();

    // Empty visitor data or if there are new decisions, create a new snapshot
    // Initialize with all exisiting decisions and any new decision events
    if (_.isEmpty(currentVisitor.snapshots) || !_.isEmpty(this.__state.decisionEvents)) {
      this.__createSnapshot();
    }

    // Add the current event
    this.__getCurrentSnapshot().events.push(payload.event);

    // TODO(tyler): do this transformation only on finalize?
    this.__state.decisions = payload.decisions;

    this.__setDirty(true);
  },

  /**
   * Registers a decision and decision event with payload
   * @param {Object} payload
   * @param {BatchDecisionTicket[]} payload.decisions
   * @param {BatchEventTicket} payload.decisionEvent
   * @private
   */
  __registerDecision: function(payload:any) {
    if (__INVARIANT__) {
      require('core/utils/invariant').isType('BatchEventTicket', payload.decisionEvent);
      if (!payload.decisions) {
        throw new Error('decisions array is required');
      }
      _.forEach(payload.decisions, function(decision) {
        require('core/utils/invariant').isType('BatchDecisionTicket', decision);
      });
    }

    this.__state.decisionEvents.push(payload.decisionEvent);

    // TODO(tyler): do this transformation only on finalize?
    this.__state.decisions = payload.decisions;

    this.__setDirty(true);
  },

  /**
   * Registers a visitor with payload
   * @param {Object} payload
   * @param {EventBatch} payload.data
   * @param {BatchVisitor} payload.visitor
   * @param {BatchDecisionTicket[]} payload.decisions
   * @private
   */
  __registerVisitor: function(payload:any) {
    if (__INVARIANT__) {
      require('core/utils/invariant').isType('EventBatch', payload.data);
      require('core/utils/invariant').isType('BatchVisitor', payload.visitor);
    }

    //Register the base state
    if (_.isEmpty(this.__state.data)) {
      this.__state.data = payload.data;
    } else {
      this.__finalizeSnapshot();
    }
    this.__state.data.visitors.push(payload.visitor);
    this.__state.decisions = payload.decisions;
    this.__state.decisionEvents = [];

    this.__setDirty(true);
  },

  __registerPreviousBatch: function(payload) {
    this.__state.previousBatches.push(payload);
    this.__setDirty(true);
  },

  /**
   * Resets the data in the store
   * @private
   */
  __resetStore: function() {
    //reset everything
    this.__state = {
      polling: false,
      shouldBatch: true,
      data: {},
      decisions: [],
      decisionEvents: [],
      canSend: false,
      isDirty: false,
      previousBatches: [],
    };
    this.emitChange();
  },

  /**
   * Clear the snapshots once the event is sent
   * @private
   */
  __clearEvents: function() {
    // Maintain only last visitor data
    var currentVisitor = this.__getCurrentVisitor();
    this.__state.data.visitors = [currentVisitor];

    //reset snapshots
    currentVisitor.snapshots = [];

    this.__setDirty(true);
  },

  __resetPreviousBatches: function() {
    this.__state.previousBatches = [];
    this.__setDirty(true);
  },

  /**
   * Sets if Avro is polling to send batched events
   * @param {boolean} shouldPoll
   * @private
   */
  __setPolling: function(shouldPoll:boolean) {
    this.__state.polling = shouldPoll;
    this.emitChange();
  },

  /**
   * Sets if Avro is polling to send batched events
   * @private
   */
  __setBatching: function(shouldBatch) {
    this.__state.shouldBatch = shouldBatch;
    this.emitChange();
  },

  /**
   * Sets if Avro is using sendBeacon to send events
   * @private
   */
  __setUseBeacon: function(shouldUseBeacon) {
    this.__state.shouldUseBeacon = shouldUseBeacon;
    this.emitChange();
  },

  /**
   * Sets true if tracker_optimizely is immediately activated
   * If manually activated, will be set using an API
   * @private
   */
  __setCanSend: function(canSend) {
    this.__state.canSend = canSend;
    this.emitChange();
  },

  /**
   * Get the Event batch object to send
   * @return {EventBatch}
   */
  getEventBatch: function(): EventBatch {
    return _.cloneDeep(this.__state.data);
  },

  getPreviousBatches: function() {
    return _.cloneDeep(this.__state.previousBatches);
  },

  /**
   * Get a list of all decisions
   * @returns {BatchDecisionTicket[]}
   * @private
   */
  __getDecisionEvents: function(): BatchDecisionTicket[] {
    return this.__state.decisionEvents.slice();
  },

  /**
   * Get a list of all decisions
   * @private
   */
  __clearDecisionEvents: function() {
    this.__state.decisionEvents = [];
  },

  /**
   * Get a list of all decisions
   * @returns {BatchDecisionTicket[]}
   * @private
   */
  __getDecisions: function(): BatchDecisionTicket[] {
    return this.__state.decisions.slice();
  },

  /**
   * Returns if the polling is enabled
   * @returns {boolean}
   */
  isPolling: function(): boolean {
    return this.__state.polling;
  },

  /**
   * Returns if should batch the events
   * @returns {boolean}
   */
  shouldBatch: function(): boolean {
    return this.__state.shouldBatch;
  },

  /**
   * Returns if should use sendBeacon to send events
   * @returns {boolean}
   */
  shouldUseBeacon: function(): boolean {
    return this.__state.shouldUseBeacon;
  },

  /**
   * Gets the latest snapshot
   * @returns {BatchEventSnapshot}
   * @private
   */
  __getCurrentSnapshot: function(): BatchEventSnapshot {
    return _.last(this.__getCurrentVisitor().snapshots);
  },

  /**
   * Gets the last visitor
   * @returns {BatchVisitor}
   * @private
   */
  __getCurrentVisitor: function(): BatchVisitor {
    return _.last(this.__state.data.visitors);
  },

  /**
   * Create Snapshot from decision events
   * @private
   */
  __createSnapshot: function() {
    var decisionEvents = this.__getDecisionEvents();
    var currentVisitor = this.__getCurrentVisitor();
    currentVisitor.snapshots.push({
      decisions: this.__getDecisions(),
      events: decisionEvents,
    });
    //Unset the decision events array
    this.__clearDecisionEvents();

    this.__setDirty(true);
  },

  /**
   * Finalize the snapshot to send
   * @private
   */
  __finalizeSnapshot: function() {
    if (this.__state.decisionEvents.length > 0) {
      this.__createSnapshot();
    }
  },

  /**
   * Check if there is data to be sent
   * @return {boolean}
   */
  hasEventsToSend: function(): boolean {
    if (!_.isEmpty(this.__state.decisionEvents)) {
      return true;
    }
    if (!_.isEmpty(this.__state.data)) {
      var hasSomeSnapshot = _.some(this.__state.data.visitors || [], function(visitor) {
        return visitor.snapshots.length > 0;
      });
      if (hasSomeSnapshot) {
        return true;
      }
    }
    return false;
  },

  hasPreviousBatchesToSend: function() {
    return !_.isEmpty(this.__state.previousBatches);
  },

  /**
   * Check if tracker_optimizely is activated
   * @return {boolean}
   */
  canSend: function(): boolean {
    return this.__state.canSend;
  },

  /**
   * Update the current visitor attributes
   * @param {VisitorAttributePayload[]} payload.attributes
   * @private
   */
  __updateAttributes: function(payload) {
    var currentVisitor = this.__getCurrentVisitor();
    if (currentVisitor) {
      currentVisitor.attributes = payload.attributes;
    }
  }
};
